import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CreditCard, Check, Shield, Trophy, Video, Calendar, Target, Clock, Gift, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Membership() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchMembershipStatus();
    }
  }, [user]);

  const fetchMembershipStatus = async () => {
    try {
      const response = await fetch(`${API}/membership/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        setMembershipStatus(await response.json());
      }
    } catch (error) {
      console.error('Error fetching membership status:', error);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);

    try {
      const response = await fetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create checkout session');
        setProcessing(false);
      }
    } catch (error) {
      toast.error('An error occurred');
      console.error(error);
      setProcessing(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  const isMember = membershipStatus?.is_member ?? user.is_member;
  const isTrial = membershipStatus?.is_trial;
  const daysRemaining = membershipStatus?.days_remaining ?? 0;
  const isExpired = membershipStatus?.is_expired;
  const expiresDate = user.membership_expires ? new Date(user.membership_expires).toLocaleDateString() : null;

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} />

      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-bundle-green mb-2">Membership</h1>
          <p className="font-body text-gray-600">
            {isMember ? 'Manage your Bundle Golf Club membership' : 'Join The Bundle Golf Club'}
          </p>
        </div>

        {/* Current Status - Free Trial */}
        {isMember && isTrial && !isExpired && (
          <Card className="border-2 border-bundle-gold bg-gradient-to-br from-bundle-gold/10 to-bundle-cream mb-8">
            <CardContent className="p-6 md:p-8 text-center">
              <div className="w-20 h-20 bg-bundle-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-bundle-green mb-2">Free Trial Active</h2>
              <p className="font-body text-gray-600 mb-4">
                You have <span className="font-bold text-bundle-green text-xl">{daysRemaining} days</span> remaining in your free trial
              </p>
              <div className="flex items-center justify-center gap-2 text-bundle-gold-dark">
                <Clock className="w-4 h-4" />
                <span className="font-body text-sm">Trial expires on {expiresDate}</span>
              </div>
              
              <div className="mt-6 pt-6 border-t border-bundle-gold/30">
                <p className="font-body text-sm text-gray-600 mb-4">
                  Love the club? Subscribe now to continue after your trial ends.
                </p>
                <Button
                  data-testid="subscribe-btn"
                  onClick={handlePayment}
                  disabled={processing}
                  className="btn-bundle"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Subscribe for $30/month
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Status - Paid Member */}
        {isMember && !isTrial && !isExpired && (
          <Card className="border-2 border-bundle-gold bg-bundle-gold/5 mb-8">
            <CardContent className="p-6 md:p-8 text-center">
              <div className="w-20 h-20 bg-bundle-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-bundle-gold" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-bundle-green mb-2">Active Member</h2>
              <p className="font-body text-gray-600">
                Your membership is active until <span className="font-semibold text-bundle-green">{expiresDate}</span>
              </p>
              <p className="font-body text-sm text-gray-500 mt-2">
                {daysRemaining} days remaining
              </p>
              
              <div className="mt-6 pt-6 border-t border-bundle-gold/30">
                <Button
                  data-testid="renew-btn"
                  onClick={handlePayment}
                  disabled={processing}
                  variant="outline"
                  className="border-bundle-green text-bundle-green hover:bg-bundle-green hover:text-white"
                >
                  {processing ? 'Processing...' : 'Renew Membership - $30'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expired or Non-Member */}
        {(!isMember || isExpired) && (
          <Card className="border-2 border-bundle-gold bg-white mb-8 overflow-hidden">
            <div className="bg-gradient-bundle p-6 text-center">
              <h2 className="font-heading text-3xl font-bold text-white mb-1">The Bundle Golf Club</h2>
              <p className="font-body text-bundle-cream/80">Golf's Ultimate Money Game</p>
            </div>
            
            <CardContent className="p-6 md:p-8">
              {/* Trial Banner */}
              <div className="bg-bundle-gold/10 border border-bundle-gold rounded-lg p-4 mb-6 text-center">
                <Gift className="w-8 h-8 text-bundle-gold mx-auto mb-2" />
                <p className="font-heading font-bold text-bundle-green text-lg">90 Days Free Trial!</p>
                <p className="font-body text-sm text-gray-600">New members get full access free for 90 days</p>
              </div>

              <div className="text-center mb-8">
                <p className="font-body text-gray-500 mb-2">After trial</p>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="font-heading text-5xl font-bold text-bundle-green">$30</span>
                  <span className="font-body text-xl text-gray-500">/month</span>
                </div>
                <p className="font-body text-gray-600">Full access to all features</p>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Video, text: 'Unlimited bundle stories' },
                  { icon: Trophy, text: 'Leaderboard access' },
                  { icon: Target, text: 'USGA handicap tracking' },
                  { icon: Calendar, text: 'Event signups' },
                  { icon: MessageCircle, text: 'Member chat & DMs' },
                  { icon: Shield, text: 'Priority support' }
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-bundle-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-4 h-4 text-bundle-green" />
                    </div>
                    <span className="font-body text-sm text-bundle-green">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Payment Button */}
              {isExpired ? (
                <Button
                  data-testid="pay-membership-btn"
                  onClick={handlePayment}
                  disabled={processing}
                  className="w-full btn-bundle py-6 text-lg"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Reactivate - $30/month
                    </>
                  )}
                </Button>
              ) : (
                <div className="text-center">
                  <p className="font-body text-bundle-green font-semibold mb-2">
                    Sign in to start your free trial!
                  </p>
                  <p className="font-body text-sm text-gray-500">
                    New members automatically get 90 days free
                  </p>
                </div>
              )}

              {/* Security Note */}
              <div className="flex items-center justify-center gap-2 mt-4 text-gray-500">
                <Shield className="w-4 h-4" />
                <span className="font-body text-sm">Secure payment powered by Stripe</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Why Join */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-6">
              <Trophy className="w-8 h-8 text-bundle-gold mb-3" />
              <h3 className="font-heading text-lg font-semibold text-bundle-green mb-2">Compete & Win</h3>
              <p className="font-body text-sm text-gray-600">
                Track your bundle wins and climb the leaderboard among fellow members.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-6">
              <Video className="w-8 h-8 text-bundle-gold mb-3" />
              <h3 className="font-heading text-lg font-semibold text-bundle-green mb-2">Share The Glory</h3>
              <p className="font-body text-sm text-gray-600">
                Post videos and photos of your winning moments for the club to see.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
